type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === 'object'
    ? value as UnknownRecord
    : null;
}

function normalizeMessage(value: unknown): string | null {
  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized || null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const normalized = normalizeMessage(item);
      if (normalized) return normalized;
    }
  }
  return null;
}

/**
 * Return one field from an Axios-shaped error response without trusting the
 * thrown value. Native errors and arbitrary rejected values return undefined.
 */
export function getApiErrorField(error: unknown, field: string): unknown {
  const errorRecord = asRecord(error);
  const response = asRecord(errorRecord?.response);
  const responseData = asRecord(response?.data);
  return responseData?.[field];
}

/**
 * Resolve the HTTP status from an Axios-shaped error.
 *
 * @returns A finite HTTP status number, or null when the rejected value does
 * not expose one.
 */
export function getApiErrorStatus(error: unknown): number | null {
  const errorRecord = asRecord(error);
  const response = asRecord(errorRecord?.response);
  const status = Number(response?.status);
  return Number.isFinite(status) ? status : null;
}

/**
 * Convert an unknown rejected value into a user-facing error message.
 *
 * API fields are checked in order, followed by the native Error message and
 * the provided fallback. Arrays from serializer validation are reduced to
 * their first meaningful scalar message.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback: string,
  fields: readonly string[] = ['error', 'detail', 'message'],
): string {
  for (const field of fields) {
    const message = normalizeMessage(getApiErrorField(error, field));
    if (message) return message;
  }

  if (error instanceof Error) {
    const message = error.message.trim();
    if (message) return message;
  }

  const errorRecord = asRecord(error);
  const recordMessage = normalizeMessage(errorRecord?.message);
  return recordMessage || fallback;
}
